import { languageId } from "../../../services/localeCodes";
import React, { useState } from "react";
import { Alert, Button, Checkbox, DatePicker, Form, Input, InputNumber, Modal, Result, Typography } from "antd";
import dayjs from "dayjs";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { applyLang } from "../../../i18n";
import ApiService from "../../../services/ApiService";
import PhoneInput from "../../../components/WebEditor/components/PhoneInput";

const { Title, Text } = Typography;

const modalStyles = `
.tr-jm-modal .ant-modal-content { border-radius: 18px; }
.tr-jm-modal .ant-modal-header { border-radius: 18px 18px 0 0; padding: 24px 28px 12px; border-bottom: 1px solid #f0f0f5; }
.tr-jm-modal .ant-modal-title h4 { margin: 0; font-size: 22px; font-weight: 800; letter-spacing: -0.01em; color: #1a1a2e; }
.tr-jm-modal .ant-modal-body { padding: 20px 28px 28px; }
.tr-jm-subtitle { color: #6b7280; font-size: 14px; margin: 4px 0 18px; }
.tr-jm-responsable {
  background: #f8fafc;
  border-radius: 12px;
  padding: 14px 16px;
  margin: 14px 0 18px;
  font-size: 13px;
  color: #4b5563;
  text-align: center;
}
.tr-jm-responsable strong { color: #1a1a2e; }
.tr-jm-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 600px) {
  .tr-jm-row { grid-template-columns: 1fr; }
  .tr-jm-modal .ant-modal-header,
  .tr-jm-modal .ant-modal-body { padding-left: 18px; padding-right: 18px; }
}
.tr-jm-services { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 8px; }
.tr-jm-services .ant-checkbox-wrapper {
  margin: 0;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
}
`;

export default function JourneyModal({ context, open, onClose }) {
    const { travel, lang } = context;
    const siteId = travel?.siteId;

    const services = (travel?.services || [])
        .filter((s) => s.active !== false)
        .map((s) => {
            const localized = applyLang(s, lang);
            return { id: s.id, label: localized?.label || "" };
        })
        .filter((s) => s.id && s.label);

    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    function handleClose() {
        // Reset l'état quand le modal se ferme
        setError(null);
        setSuccess(false);
        form.resetFields();
        onClose && onClose();
    }

    async function onFinish(values) {
        setError(null);

        const range = values.range || [];
        const startDate = range[0];
        const endDate = range[1];

        // RangePicker garantit déjà l'ordre via disabledDate, double-check par sécurité
        if (!startDate || !endDate || endDate.isBefore(startDate, "day")) {
            setError(t`Les dates de séjour sont invalides.`);
            return;
        }

        // PhoneInput renvoie NaN si invalide, null/undefined si vide
        if (Number.isNaN(values.tel)) {
            setError(t`Le numéro de téléphone est invalide.`);
            return;
        }

        setLoading(true);
        try {
            const selectedServiceLabels = services
                .filter((s) => (values.services || []).includes(s.id))
                .map((s) => s.label);

            const res = await ApiService.post(`/travel/${siteId}/journey/submit`, {
                lang: languageId(context.langValue || lang),
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
                adults: values.adults || 1,
                children: values.children || 0,
                prenom: values.prenom,
                nom: values.nom,
                email: values.email,
                tel: values.tel || "",
                services: selectedServiceLabels,
                comment: values.comment || "",
            });

            if (res?.success) {
                setSuccess(true);
            } else {
                setError(
                    res?.error === "too_many_requests"
                        ? t`Une demande vient d'être envoyée. Merci de patienter une minute.`
                        : t`Une erreur est survenue. Merci de réessayer.`
                );
            }
        } catch (e) {
            setError(t`Une erreur est survenue. Merci de réessayer.`);
        }
        setLoading(false);
    }

    // Désactive toutes les dates avant aujourd'hui
    const disabledDate = (current) => current && current.isBefore(dayjs().startOf("day"));

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: modalStyles }} />
            <Modal
                className="tr-jm-modal"
                open={open}
                onCancel={handleClose}
                footer={null}
                width={640}
                centered
                destroyOnClose
                title={success ? null : <Title level={4}><Trans>Annoncez votre venue</Trans></Title>}
            >
                {success ? (
                    <Result
                        status="success"
                        title={t`Votre venue est confirmée`}
                        subTitle={
                            <span>
                                <Trans>Nous avons bien reçu votre annonce. Vous allez recevoir un email de confirmation.</Trans>
                                <br />
                                <Trans>Nous nous réjouissons de vous accueillir au Beth Habad.</Trans>
                            </span>
                        }
                        extra={
                            <Button type="primary" onClick={handleClose}>
                                <Trans>Fermer</Trans>
                            </Button>
                        }
                    />
                ) : (
                    <>
                        <Text className="tr-jm-subtitle">
                            <Trans>Faites-nous savoir quand vous arrivez pour que nous puissions vous accueillir au mieux.</Trans>
                        </Text>

                        {error && (
                            <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />
                        )}

                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onFinish}
                            initialValues={{ adults: 1, children: 0 }}
                            requiredMark={false}
                        >
                            <Form.Item
                                label={t`Dates du séjour`}
                                name="range"
                                rules={[{ required: true, message: t`Merci de sélectionner vos dates` }]}
                            >
                                <DatePicker.RangePicker
                                    style={{ width: "100%" }}
                                    format="DD/MM/YYYY"
                                    disabledDate={disabledDate}
                                    placeholder={[t`Arrivée`, t`Départ`]}
                                />
                            </Form.Item>

                            <div className="tr-jm-row">
                                <Form.Item
                                    label={t`Adultes`}
                                    name="adults"
                                    rules={[{ required: true, message: t`Champ requis` }]}
                                >
                                    <InputNumber min={1} max={50} style={{ width: "100%" }} />
                                </Form.Item>
                                <Form.Item label={t`Enfants`} name="children">
                                    <InputNumber min={0} max={50} style={{ width: "100%" }} />
                                </Form.Item>
                            </div>

                            <div className="tr-jm-row">
                                <Form.Item
                                    label={t`Prénom`}
                                    name="prenom"
                                    rules={[{ required: true, message: t`Champ requis` }]}
                                >
                                    <Input autoComplete="given-name" />
                                </Form.Item>
                                <Form.Item
                                    label={t`Nom`}
                                    name="nom"
                                    rules={[{ required: true, message: t`Champ requis` }]}
                                >
                                    <Input autoComplete="family-name" />
                                </Form.Item>
                            </div>

                            <div className="tr-jm-row">
                                <Form.Item
                                    label={t`Email`}
                                    name="email"
                                    rules={[
                                        { required: true, message: t`Champ requis` },
                                        { type: "email", message: t`Email invalide` },
                                    ]}
                                >
                                    <Input autoComplete="email" />
                                </Form.Item>
                                <Form.Item
                                    label={t`Téléphone`}
                                    name="tel"
                                    rules={[{ required: true, message: t`Champ requis` }]}
                                >
                                    <PhoneInput defaultLang={lang?.value || lang} placeholder={t`Téléphone`} />
                                </Form.Item>
                            </div>

                            {services.length > 0 && (
                                <Form.Item label={t`Services qui vous intéressent`} name="services">
                                    <Checkbox.Group className="tr-jm-services">
                                        {services.map((s) => (
                                            <Checkbox key={s.id} value={s.id}>
                                                {s.label}
                                            </Checkbox>
                                        ))}
                                    </Checkbox.Group>
                                </Form.Item>
                            )}

                            <Form.Item label={t`Un mot pour le Beth Habad ?`} name="comment">
                                <Input.TextArea
                                    rows={3}
                                    placeholder={t`Vos demandes particulières, questions…`}
                                />
                            </Form.Item>

                            <div className="tr-jm-responsable">
                                <Trans>Votre demande sera transmise à l'équipe du Beth Habad.</Trans>
                            </div>

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 }}>
                                <Button onClick={handleClose}><Trans>Annuler</Trans></Button>
                                <Button type="primary" htmlType="submit" loading={loading}>
                                    <Trans>Envoyer ma demande</Trans>
                                </Button>
                            </div>
                        </Form>
                    </>
                )}
            </Modal>
        </>
    );
}
